import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Edit3, ExternalLink, Type } from "react-feather";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { RichTextEditor } from "@/components/Editor/RichTextEditor";
import {
  programService,
  JoinContentFields,
} from "@/services/program.service";
import { getRichTextLimitError } from "@/utils/richText";
import { Button } from "@/components/ui/button";

const FIELDS = [
  {
    key: "joinTitle" as const,
    label: "Join title",
    help: "The heading on the join card.",
    kind: "text" as const,
    maxLength: 200,
    fallback: "Join the team",
  },
  {
    key: "joinText" as const,
    label: "Join description",
    help: "The pitch shown to affiliates who haven't joined yet.",
    kind: "rich" as const,
    maxLength: 4000,
    fallback: "Become part of us. Get paid for your hard work.",
  },
  {
    key: "ambassadorText" as const,
    label: "Ambassador message",
    help: "Shown once an affiliate has joined this program.",
    kind: "rich" as const,
    maxLength: 4000,
    fallback: "Welcome to our brand",
  },
];

type FieldKey = (typeof FIELDS)[number]["key"];

interface JoinContentCardProps {
  programId: string;
}

/** Strip tags so a rich-text default can be previewed on one line. */
function toPlainText(html: string | null): string {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Per-program join page copy.
 *
 * Every program — including the discoverable one — inherits the brand's copy by
 * default, so a brand that doesn't care never touches this. Switching a field to
 * "Customize" stores an override for this program only; switching back to "Use
 * brand default" clears it (sends null).
 *
 * The brand default itself lives on BrandAppearance and is edited in Settings →
 * Appearance. It is deliberately not stored on the discoverable program: brands
 * can move that flag between programs at will.
 */
export function JoinContentCard({ programId }: JoinContentCardProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["program-join-content", programId],
    queryFn: () => programService.getJoinContent(programId),
    enabled: !!programId,
  });

  // `null` in draft = inherit. Mirrors the wire format exactly, so saving is a
  // straight pass-through with no "did they mean blank or inherit?" guessing.
  const [draft, setDraft] = useState<JoinContentFields | null>(null);

  useEffect(() => {
    if (data) setDraft({ ...data.overrides });
  }, [data]);

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: (content: JoinContentFields) =>
      programService.updateJoinContent(programId, content),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["program-join-content", programId],
      });
      await queryClient.invalidateQueries({ queryKey: ["program", programId] });
      toast.success("Join page content saved");
    },
    onError: (err: unknown) => {
      toast.error(
        (err as AxiosError<{ message: string }>)?.response?.data?.message ||
          "Failed to save join page content"
      );
    },
  });

  const hasChanges = useMemo(() => {
    if (!data || !draft) return false;
    return FIELDS.some(
      ({ key }) => (draft[key] ?? null) !== (data.overrides[key] ?? null)
    );
  }, [data, draft]);

  const overriddenCount = useMemo(
    () => (draft ? FIELDS.filter(({ key }) => draft[key] !== null).length : 0),
    [draft]
  );

  const handleSave = () => {
    if (!draft) return;

    for (const field of FIELDS) {
      if (field.kind !== "rich") continue;
      const value = draft[field.key];
      if (!value) continue;
      const error = getRichTextLimitError(value, field.maxLength);
      if (error) {
        toast.error(`${field.label}: ${error}`);
        return;
      }
    }

    save(draft);
  };

  const setField = (key: FieldKey, value: string | null) =>
    setDraft((current) => (current ? { ...current, [key]: value } : current));

  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left"
        aria-expanded={isOpen}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
          <Type className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-foreground">
            Join page content
          </span>
          <span className="block text-xs text-muted-foreground">
            {overriddenCount > 0
              ? `${overriddenCount} of ${FIELDS.length} customized for this program`
              : "Inheriting your brand default"}
          </span>
        </span>
        <ChevronDown
          className={`w-5 h-5 text-muted-foreground transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="border-t border-border px-5 py-5 space-y-5">
          {isLoading || !draft || !data ? (
            <div className="space-y-4 animate-pulse">
              {FIELDS.map((field) => (
                <div key={field.key} className="space-y-2">
                  <div className="h-3 w-32 bg-muted rounded" />
                  <div className="h-10 bg-muted rounded-lg" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="flex gap-3 rounded-lg bg-brand-50 p-3 text-sm ring-1 ring-brand-100">
                <Edit3 className="w-5 h-5 shrink-0 text-brand-600" />
                <p className="text-muted-foreground">
                  Each field follows your brand default until you customize it.
                  Customize only what should read differently for this program —
                  anything you leave alone keeps following the brand. Edit the
                  brand default itself in{" "}
                  <a
                    href="/brand/settings?tab=appearance-settings"
                    className="font-medium text-brand-700 hover:underline inline-flex items-center gap-1"
                  >
                    Appearance settings
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  .
                </p>
              </div>

              {FIELDS.map((field) => {
                const value = draft[field.key];
                const isOverridden = value !== null;
                const brandDefault = data.brandDefaults[field.key];
                const defaultPreview =
                  toPlainText(brandDefault) || field.fallback;

                return (
                  <div key={field.key} className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground">
                          {field.label}
                        </label>
                        <p className="text-[11px] text-muted-foreground">{field.help}</p>
                      </div>

                      <div className="flex items-center gap-1 rounded-lg bg-muted p-0.5 text-[11px] font-medium">
                          <button
                            type="button"
                            onClick={() => setField(field.key, null)}
                            className={`rounded-md px-2.5 py-1 transition-colors ${
                              !isOverridden
                                ? "bg-card text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            Use brand default
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setField(field.key, brandDefault ?? "")
                            }
                            className={`rounded-md px-2.5 py-1 transition-colors ${
                              isOverridden
                                ? "bg-card text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            Customize
                          </button>
                        </div>
                    </div>

                    {!isOverridden ? (
                      <div className="rounded-lg border border-dashed border-border bg-muted/60 px-3 py-2.5">
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {defaultPreview}
                        </p>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          Inherited from your brand default
                        </p>
                      </div>
                    ) : field.kind === "text" ? (
                      <input
                        type="text"
                        value={value ?? ""}
                        onChange={(e) => setField(field.key, e.target.value)}
                        maxLength={field.maxLength}
                        placeholder={defaultPreview}
                        className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
                      />
                    ) : (
                      <RichTextEditor
                        content={value ?? ""}
                        onChange={(content) => setField(field.key, content)}
                        placeholder={defaultPreview}
                        maxLength={field.maxLength}
                      />
                    )}
                  </div>
                );
              })}

              {hasChanges && (
                <div className="flex justify-end gap-3 border-t border-border pt-4">
                  <button
                    type="button"
                    onClick={() => setDraft({ ...data.overrides })}
                    className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                  >
                    Discard
                  </button>
                  <Button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    <span>Save changes</span>
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

export default JoinContentCard;
